/**
 * One payment-proof file, drawn small.
 *
 * Shared by the payments table and the verify dialog so a receipt looks the same wherever an
 * operator meets it — and, more usefully, so the three states that are NOT a picture stay
 * consistent. Those states are the point of this component: a proof is routinely a PDF, and a
 * proof whose file has gone missing from storage still has a row in the database, so "there is
 * an image here" is the exception the other cases have to be told apart from.
 *
 * The bytes are fetched through the API client rather than put straight in `src`, because the
 * download endpoint needs the admin bearer token; a bare `<img src>` would send no auth and
 * render a broken icon. Fetching starts only once the tile scrolls into view, so a fifty-row
 * page does not pull fifty images nobody looked at, and what reaches the `<img>` is resampled
 * to `thumbnailWidth` first — a proof is a phone photo of up to 5 MB, and decoding one of those
 * per row is what made the table stutter. See `lib/downscaleImage`.
 */

import { useRef } from "react";
import { FileText, ImageOff } from "lucide-react";

import { useAuthedObjectUrl } from "@/hooks/useAuthedObjectUrl";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import { isRenderableImage, proofDownloadPath } from "@/lib/paymentProof";
import type { PaymentProofDto } from "@/services/admin-payment.service";
import { cn } from "@/lib/utils";

/** The extension, upper-cased, for the tiles that show a label instead of a picture. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  const ext = dot > -1 ? fileName.slice(dot + 1) : "";
  return ext ? ext.toUpperCase().slice(0, 4) : "FILE";
}

interface ProofThumbnailProps {
  proof: PaymentProofDto;
  /** Set on tiles small enough that a text label would not fit. */
  compact?: boolean;
  /**
   * Width, in device pixels, to resample the image to. Size it to the tile times the pixel
   * ratio it will be seen at — a 40px tile on a 2x screen needs 80 — and round up.
   */
  thumbnailWidth?: number;
  className?: string;
}

export function ProofThumbnail({
  proof,
  compact = false,
  thumbnailWidth = 256,
  className,
}: ProofThumbnailProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isVisible = useInViewOnce(containerRef);
  const renderable = isRenderableImage(proof);
  const { objectUrl, isError } = useAuthedObjectUrl(
    renderable ? proofDownloadPath(proof.downloadUrl) : null,
    isVisible,
    thumbnailWidth,
  );

  const fallback = (
    icon: React.ReactNode,
    label: string,
    tone: string,
  ) => (
    <div
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-0.5 bg-muted/60",
        tone,
      )}
    >
      {icon}
      {/* The label is what separates "a PDF receipt" from "the picture failed" from "nothing
          was uploaded" — three states that were previously three grey squares. */}
      {!compact && (
        <span className="text-[9px] font-semibold leading-none tracking-wide">
          {label}
        </span>
      )}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={cn("h-full w-full", className)}
      title={proof.fileName}
    >
      {!renderable
        ? fallback(
            <FileText className="h-4 w-4" />,
            extensionOf(proof.fileName),
            "text-muted-foreground",
          )
        : isError
          ? fallback(
              <ImageOff className="h-4 w-4" />,
              "N/A",
              "text-destructive-ink",
            )
          : objectUrl
            ? (
                <img
                  src={objectUrl}
                  alt={proof.fileName}
                  loading="lazy"
                  // Decoded off the main thread. No width/height: the tile is a fixed square
                  // sized by its parent, and the resampled blob keeps the photo's aspect ratio,
                  // so any pair of numbers here would describe the image wrongly without
                  // changing how it lays out.
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              )
            : <div className="h-full w-full animate-pulse bg-muted" />}
    </div>
  );
}
